/**
 * Chat betting data-card types (settled hit rate + direction prior).
 *
 * Leaf types split out of chatTypes.ts to keep that barrel under the file-size limit. No
 * dependencies on chatTypes; chatTypes imports the two *CardData types back for the DataCard
 * union. Consumers import these directly from here.
 */

/** One settled-rate cell: hits over settled attempts. hitRate null when total is 0. */
export interface SettledRate {
  hits: number;
  total: number;
  hitRate: number | null;
}

/**
 * Line-aware rate for a player's prop over one direction. `clears` (how often the actual value
 * cleared the TARGET line) is the headline; `naive` (how often the pick hit its own line) is
 * secondary. avgActual + lineRange give the pooled-line context.
 */
export interface LineAwareRate {
  /** actual value cleared the target line. null when no target line was resolved. */
  clears: SettledRate | null;
  /** hit vs the line each pick was set at (line-naive). Always present. */
  naive: SettledRate;
  /** average actual stat value over the sample. */
  avgActual: number | null;
  /** min/max of the lines the history was set at (surfaces the pooling). */
  lineRange: { min: number; max: number } | null;
}

/**
 * USER-FACING label for the independent second model. The upstream source (Action Network) is
 * NEVER shown in-app: naming a third party whose data we ingest via a spoofed User-Agent invites
 * trademark + ToS exposure. Internal code/DB/plans keep the real name; every consumer-facing
 * string (card + LLM summary) uses THIS label only.
 */
export const INDEPENDENT_MODEL_LABEL = 'Independent model';

/**
 * The independent second model's read for one direction of a prop. Its projection/grade/edge
 * cross-check ours. `conflict` is line-aware: the model grades vs its OWN line, so the grade only
 * speaks to our side when anLine === our target line; when the lines differ, conflict is decided
 * by the projection vs our line instead (see actionNetworkRead). `coverage: 'not_covered'` means
 * the model had no line for this player/prop (absence, NOT agreement).
 */
export interface ActionNetworkRead {
  coverage: 'covered' | 'not_covered';
  /** Letter grade AN assigned this side at AN's own line (e.g. "B+", "F"). */
  grade?: string;
  /** AN's projected stat value. */
  projection?: number;
  /** AN's signed edge for this side (positive = AN likes it). */
  edge?: number;
  /** The line AN graded against (book-agnostic core line); may differ from our target line. */
  anLine?: number;
  /** AN grades our side unfavorably (line-aware). Surfaced as an explicit conflict warning. */
  conflict?: boolean;
}

/** Per-direction line-aware hit rate for a player's prop: global model rate + the user's own rate. */
export interface PlayerPropHitRateCardData {
  playerName: string;
  /** Display label for the prop, e.g. "Rebounds". */
  propType: string;
  league: string;
  /** Book whose line was used for the target (lines are book-specific). */
  sportsbook: string;
  directions: Array<{
    direction: 'over' | 'under';
    /** target line evaluated; null when none resolvable (then clears is null). */
    line: number | null;
    /** where the target line came from. */
    lineSource?: 'live' | 'pick';
    /** the pick-line fallback was old. */
    lineIsStale?: boolean;
    global: LineAwareRate;
    /** null when the user has no settled bets on this player+prop+direction. */
    personal: LineAwareRate | null;
    /** Action Network's independent read for this direction. Optional (absent = not fetched). */
    actionNetwork?: ActionNetworkRead;
  }>;
}

/** Empirical direction-prior base rates for a prop in a league (both sides when unfiltered). */
export interface DirectionPriorCardData {
  league: string;
  /** Display label for the prop, e.g. "Rebounds". */
  propType: string;
  /** Break-even hit rate at standard -110 juice, for reference. */
  breakeven: number;
  cells: Array<{
    direction: 'over' | 'under';
    hitRate: number | null;
    sampleCount: number;
    /** sampleCount >= the reliability threshold. */
    usable: boolean;
  }>;
}
